import { CheckCircleOutlined } from "@ant-design/icons";
import type { CardProps } from "antd";
import { Descriptions, Tag } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useNavigate } from "react-router";

import { DashboardCardContainer } from "src/app/features/Dashboard/utils/dashboardStyles";
import { SecondaryButton } from "../../../../components/Buttons/components/ActionButton";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import { Palette } from "../../../../core/styles/palette";
import type { DataGroupInfoDto } from "../types/Dashboard";

interface DataGroupInfoCardProps extends CardProps {
    dataGroupInfo: DataGroupInfoDto;
}

/**
 * DataGroupInfoCard - Displays data group information in sidebar
 */
export const DataGroupInfoCard: FC<DataGroupInfoCardProps> = ({ dataGroupInfo, ...cardProps }) => {
    const navigate = useNavigate();

    const getStatusColor = (status: string): string => {
        const statusMap: Record<string, string> = {
            ACTIVE: Palette.HeroGreen,
            INACTIVE: Palette.LightPink,
            PENDING: Palette.Warning,
        };
        return statusMap[status.toUpperCase()] || "default";
    };

    const handleChange = (record: DataGroupInfoDto) => {
        navigate(`${BASE_PATH}/${ResourceName.DATA_GROUPS}/${FORM_ACTION.EDIT}/${record.id}`);
    };

    return (
        <DashboardCardContainer
            title={i18n.t("common.dataGroup")}
            extra={
                <SecondaryButton size="small" onClick={() => handleChange(dataGroupInfo)}>
                    {i18n.t("dashboard.showMore")}
                </SecondaryButton>
            }
            {...cardProps}
        >
            <Descriptions column={1} size="small">
                <Descriptions.Item label={i18n.t("dataGroup.name")}>
                    <strong>{dataGroupInfo.name}</strong>
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("dataGroup.status")}>
                    <Tag color={getStatusColor(dataGroupInfo.status)} icon={<CheckCircleOutlined />}>
                        {dataGroupInfo.status}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("dashboard.dataGroupInfo.code")}>
                    <span>{dataGroupInfo.code}</span>
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("dashboard.dataGroupInfo.timezone")}>{dataGroupInfo.timeZone}</Descriptions.Item>
                <Descriptions.Item label={i18n.t("dashboard.dataGroupInfo.tidTemplate")}>
                    {dataGroupInfo.tidTemplate || i18n.t("enum.updateStatus.na")}
                </Descriptions.Item>
            </Descriptions>
        </DashboardCardContainer>
    );
};
